/**
 * Cognito-backed auth context.
 * Tokens kept in memory only — no localStorage. Right default for a trading
 * terminal where session theft is high-impact.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  CognitoUser, CognitoUserPool, CognitoUserAttribute, AuthenticationDetails, CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { trackLogin } from '../lib/analytics';

export interface AuthEnv {
  userPoolId: string;
  userPoolClientId: string;
}

export interface AuthState {
  status: 'signed_out' | 'mfa_required' | 'signed_in' | 'error' | 'sign_up_pending' | 'forgot_pending';
  username?: string;
  displayName?: string;
  error?: string;
  mfaSatisfied: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  completeMfa: (code: string) => Promise<void>;
  signUp: (email: string, password: string, name: string, firstName?: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => void;
  getIdToken: () => Promise<string | undefined>;
}

/** Exported so the offline demo can mount a mock provider without monkey-patching. */
export const AuthCtx = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth outside provider');
  return v;
}

export function AuthProvider({ env, children }: { env: AuthEnv; children: React.ReactNode }) {
  const pool = useMemo(() => new CognitoUserPool({
    UserPoolId: env.userPoolId,
    ClientId: env.userPoolClientId,
  }), [env.userPoolId, env.userPoolClientId]);

  const [user, setUser] = useState<CognitoUser | undefined>();
  const [session, setSession] = useState<CognitoUserSession | undefined>();
  const [status, setStatus] = useState<AuthState['status']>('signed_out');
  const [error, setError] = useState<string | undefined>();
  const [displayName, setDisplayName] = useState<string | undefined>();

  // Fetch user attributes (including first_name) when session is established
  React.useEffect(() => {
    if (!user || !session) {
      setDisplayName(undefined);
      return;
    }
    user.getUserAttributes((err, attrs) => {
      if (err) {
        console.warn('Failed to fetch user attributes:', err);
        return;
      }
      if (attrs) {
        // Try custom:first_name first (new attribute), then fall back to name
        let firstName = '';
        const customFirstName = attrs.find((a) => a.Name === 'custom:first_name');
        if (customFirstName && customFirstName.Value) {
          firstName = customFirstName.Value;
        } else {
          const nameAttr = attrs.find((a) => a.Name === 'name');
          if (nameAttr && nameAttr.Value) {
            firstName = nameAttr.Value.split(' ')[0] ?? nameAttr.Value;
          }
        }
        if (firstName) {
          setDisplayName(firstName);
        }
      }
    });
  }, [user, session]);

  const signIn = useCallback(async (username: string, password: string) => {
    setError(undefined);
    const u = new CognitoUser({ Username: username, Pool: pool });
    setUser(u);
    const auth = new AuthenticationDetails({ Username: username, Password: password });
    await new Promise<void>((resolve, reject) => {
      u.authenticateUser(auth, {
        onSuccess: (s) => {
          setSession(s);
          setStatus('signed_in');
          // Track login to Mixpanel for analytics
          trackLogin(username, username);
          resolve();
        },
        onFailure: (err) => { setStatus('error'); setError(err?.message ?? 'auth failed'); reject(err); },
        mfaRequired: () => { setStatus('mfa_required'); resolve(); },
        totpRequired: () => { setStatus('mfa_required'); resolve(); },
        newPasswordRequired: () => {
          setStatus('error'); setError('new password required — change it in the console first'); resolve();
        },
      });
    });
  }, [pool]);

  const completeMfa = useCallback(async (code: string) => {
    if (!user) { setError('not in MFA flow'); return; }
    await new Promise<void>((resolve, reject) => {
      user.sendMFACode(code, {
        onSuccess: (s) => { setSession(s); setStatus('signed_in'); resolve(); },
        onFailure: (err) => { setStatus('error'); setError(err?.message ?? 'mfa failed'); reject(err); },
      });
    });
  }, [user]);

  const signOut = useCallback(() => {
    user?.signOut();
    setUser(undefined); setSession(undefined); setStatus('signed_out');
  }, [user]);

  const signUp = useCallback(async (email: string, password: string, name: string, firstName?: string) => {
    setError(undefined);
    const attrs = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: name }),
      // Store firstName as custom attribute for easy retrieval on login
      new CognitoUserAttribute({ Name: 'custom:first_name', Value: firstName || name.split(' ')[0] || '' }),
    ];
    await new Promise<void>((resolve, reject) => {
      pool.signUp(email, password, attrs, [], (err) => {
        if (err) {
          setStatus('error');
          setError(err.message ?? 'sign-up failed');
          reject(err);
          return;
        }
        // User created, awaiting email verification code
        setStatus('sign_up_pending');
        resolve();
      });
    });
  }, [pool]);

  const confirmSignUp = useCallback(async (email: string, code: string) => {
    setError(undefined);
    const u = new CognitoUser({ Username: email, Pool: pool });
    await new Promise<void>((resolve, reject) => {
      u.confirmRegistration(code, true, (err) => {
        if (err) {
          setStatus('error');
          setError(err.message ?? 'confirmation failed');
          reject(err);
          return;
        }
        // Confirmed — return to signed_out so user can sign in fresh
        setStatus('signed_out');
        resolve();
      });
    });
  }, [pool]);

  const resendConfirmCode = useCallback(async (email: string) => {
    setError(undefined);
    const u = new CognitoUser({ Username: email, Pool: pool });
    await new Promise<void>((resolve, reject) => {
      u.resendConfirmationCode((err) => {
        if (err) {
          setError(err.message ?? 'resend failed');
          reject(err);
          return;
        }
        resolve();
      });
    });
  }, [pool]);

  const forgotPassword = useCallback(async (email: string) => {
    setError(undefined);
    const u = new CognitoUser({ Username: email, Pool: pool });
    await new Promise<void>((resolve, reject) => {
      u.forgotPassword({
        onSuccess: () => {
          // Code sent successfully — caller transitions UI to "enter code + new password"
          setStatus('forgot_pending');
          resolve();
        },
        onFailure: (err) => {
          setStatus('error');
          setError(err.message ?? 'password reset failed');
          reject(err);
        },
      });
    });
  }, [pool]);

  const confirmForgotPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    setError(undefined);
    const u = new CognitoUser({ Username: email, Pool: pool });
    await new Promise<void>((resolve, reject) => {
      u.confirmPassword(code, newPassword, {
        onSuccess: () => {
          // Password reset complete — return to signed_out so user can sign in fresh
          setStatus('signed_out');
          resolve();
        },
        onFailure: (err) => {
          setStatus('error');
          setError(err.message ?? 'confirm password failed');
          reject(err);
        },
      });
    });
  }, [pool]);

  const getIdToken = useCallback(async (): Promise<string | undefined> => {
    if (!user) return undefined;
    return new Promise((resolve) => {
      user.getSession((err: Error | null, s: CognitoUserSession | null) => {
        if (err || !s) { resolve(undefined); return; }
        resolve(s.getIdToken().getJwtToken());
      });
    });
  }, [user]);

  const mfaSatisfied = useMemo(() => {
    if (!session) return false;
    try {
      const payload = JSON.parse(atob(session.getIdToken().getJwtToken().split('.')[1] ?? ''));
      const amr = payload.amr ?? payload['cognito:amr'];
      const arr = Array.isArray(amr) ? amr : (typeof amr === 'string' ? JSON.parse(amr) : []);
      return Array.isArray(arr) && arr.map(String).some((m) => m.toLowerCase() === 'mfa');
    } catch { return false; }
  }, [session]);

  const value: AuthState = {
    status, username: user?.getUsername(), displayName, error, mfaSatisfied,
    signIn, completeMfa, signUp, confirmSignUp, resendConfirmCode,
    forgotPassword, confirmForgotPassword,
    signOut, getIdToken,
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

